/**
 * Hook for executing ANI swap transactions
 *
 * This hook handles the swap execution via the ANI Gateway Adapter contract.
 * It builds the appropriate transaction calldata based on swap direction and
 * manages token approvals.
 */

import { Interface } from '@ethersproject/abi'
import { BigNumber } from '@ethersproject/bignumber'
import { Token, TradeType } from '@uniswap/sdk-core'
import { encodeRouteToPath, FeeAmount } from '@uniswap/v3-sdk'
import { useWeb3React } from '@web3-react/core'
import { useMemo } from 'react'

import ANI_ADAPTER_ABI from '../abis/ani-adapter.json'
import { ANI_ADAPTER_ADDRESSES } from '../constants/addresses'
import { SupportedChainId } from '../constants/chains'
import { HYUSD, WRAPPED_NATIVE_CURRENCY } from '../constants/tokens'
import { HIGH_USDQ_THRESHOLD, HYUSD_BUFFER } from '../lib/ani'
import { useTransactionAdder } from '../state/transactions/hooks'
import { TransactionType } from '../state/transactions/types'
import { AniSwapCallbackState, AniSwapParams } from '../types/ani'
import { currencyId } from '../utils/currencyId'
import { useAniAdapterContract } from './useContract'
import useTransactionDeadline from './useTransactionDeadline'

// V3 path encoding helpers
/**
 * Size of fee tier in bytes for V3 path encoding.
 * Uniswap V3 uses 3 bytes (24 bits) to encode the fee tier.
 * Used in encodePath() to pad fee values to correct byte width.
 */
const FEE_SIZE = 3

// ANI swap slippage and safety constants
/**
 * Basis points denominator for slippage calculations (100% = 10000 bps)
 */
const SLIPPAGE_BASE_POINTS = 10000
/**
 * Safety margin percentage for ANI amount calculation (1%)
 * Applied to reduce ANI amount by 1% to account for price movement during transaction
 */
const ANI_SAFETY_MARGIN_PERCENT = 99
const ANI_SAFETY_MARGIN_DIVISOR = 100
/**
 * Gas buffer multiplier percentage (adds 20% to estimated gas)
 */
const GAS_BUFFER_PERCENT = 120
const GAS_BUFFER_DIVISOR = 100
/**
 * Buffer percentage for USDQ threshold in direct swaps (adds 2%)
 * This ensures enough hyUSD is pulled to cover the actual USDQ cost,
 * accounting for small discrepancies between simulation and contract math.
 * Matches anistar.io's approach of passing accurate USDQ thresholds.
 */
const USDQ_THRESHOLD_BUFFER_PERCENT = 102
const USDQ_THRESHOLD_BUFFER_DIVISOR = 100

/**
 * Encode a V3 path for swap router
 * Format: [tokenIn (20 bytes)] [fee (3 bytes)] [tokenOut (20 bytes)]
 */
function encodePath(tokens: string[], fees: FeeAmount[]): string {
  if (tokens.length !== fees.length + 1) {
    throw new Error('Invalid path/fee lengths')
  }

  let encoded = '0x'
  for (let i = 0; i < fees.length; i++) {
    // 20 byte address
    encoded += tokens[i].slice(2).toLowerCase()
    // 3 byte fee
    encoded += fees[i].toString(16).padStart(FEE_SIZE * 2, '0')
  }
  // final token
  encoded += tokens[tokens.length - 1].slice(2).toLowerCase()

  return encoded
}

/**
 * Build an empty path (for direct hyUSD <-> ANI swaps)
 */
function emptyPath(): string {
  return '0x'
}

/**
 * Hook to execute ANI swaps via the Gateway Adapter
 */
export function useAniSwapCallback(params: AniSwapParams): {
  state: AniSwapCallbackState
  callback: null | (() => Promise<string>)
  error: string | null
} {
  const { account, chainId } = useWeb3React()
  const deadline = useTransactionDeadline()
  const addTransaction = useTransactionAdder()

  const { aniQuote, inputCurrency, outputCurrency, slippageTolerance, recipient } = params

  // Get Adapter contract instance using hook
  const adapterContract = useAniAdapterContract(true)

  // Determine swap function and build calldata
  const { calldata, error, nativeValue } = useMemo(() => {
    const invalidResult = {
      calldata: null as string | null,
      error: null as string | null,
      nativeValue: BigNumber.from(0),
    }

    if (!aniQuote.direction || !aniQuote.aniAmount || !aniQuote.hyUsdAmount) {
      return { ...invalidResult, error: 'Invalid quote' }
    }

    if (!inputCurrency || !outputCurrency) {
      return { ...invalidResult, error: 'Missing currencies' }
    }

    if (!account || !deadline) {
      return { ...invalidResult, error: 'Missing account or deadline' }
    }

    const recipientAddr = recipient ?? account

    // Get wrapped native token address for path encoding when using native HYDRA
    const wrappedNative = WRAPPED_NATIVE_CURRENCY[chainId as SupportedChainId]
    const adapterAddr = ANI_ADAPTER_ADDRESSES[SupportedChainId.HYDRA]
    const hyUsdAddr = HYUSD.address

    try {
      const iface = new Interface(ANI_ADAPTER_ABI)

      if (aniQuote.direction === 'buy') {
        // BUY ANI: Token -> hyUSD -> ANI
        // When user types ANI amount: use swapTokenForExactInputANI (EXACT_OUTPUT) to guarantee exact ANI
        // When user types token amount: use swapExactInputTokenForANI (EXACT_INPUT)

        // Get input token address
        const inputTokenAddr = inputCurrency.isNative ? wrappedNative?.address : (inputCurrency as Token).address
        if (!inputTokenAddr && !aniQuote.isDirectSwap) {
          return {
            ...invalidResult,
            error: 'Could not determine input token address',
          }
        }

        // usdqThreshold = very high for BUY (not MAX_UINT256 to avoid contract reverts)
        const usdqThreshold = BigNumber.from(HIGH_USDQ_THRESHOLD)

        // Calculate slippage in basis points
        const slippageBps =
          (Number(slippageTolerance.numerator.toString()) * SLIPPAGE_BASE_POINTS) /
          Number(slippageTolerance.denominator.toString())

        if (aniQuote.isDirectSwap) {
          // Direct swap: hyUSD -> ANI (no DEX leg)
          // Use actual USDQ threshold with buffer to ensure enough hyUSD is pulled
          // This matches anistar.io's approach of passing calculated thresholds
          const baseAniAmount = BigNumber.from(aniQuote.aniAmount.quotient.toString())
          const hyUsdForHandler = BigNumber.from(aniQuote.hyUsdAmount.quotient.toString())

          // Calculate USDQ threshold from quote with 2% buffer for precision differences
          const baseUsdqThreshold = aniQuote.usdqAmount ? BigNumber.from(aniQuote.usdqAmount) : usdqThreshold
          const directSwapUsdqThreshold = baseUsdqThreshold
            .mul(USDQ_THRESHOLD_BUFFER_PERCENT)
            .div(USDQ_THRESHOLD_BUFFER_DIVISOR)

          // Calculate buffered hyUSD amount to ensure Adapter pulls enough for the threshold
          // hyUSD_buffered = hyUSD_base * (usdqThreshold_buffered / usdqThreshold_base)
          // This ensures the Adapter pulls enough hyUSD to cover the USDQ needed
          const hyUsdBuffered = aniQuote.usdqAmount
            ? hyUsdForHandler.mul(directSwapUsdqThreshold).div(baseUsdqThreshold)
            : hyUsdForHandler

          const handlerParams = {
            aniAmount: baseAniAmount,
            usdqThreshold: directSwapUsdqThreshold,
          }

          const routerParams = {
            path: emptyPath(),
            recipient: adapterAddr,
            amountOut: hyUsdBuffered,
            amountInMaximum: hyUsdBuffered, // Pull buffered amount to cover USDQ threshold
          }

          const encoded = iface.encodeFunctionData('swapTokenForExactInputANI', [
            [routerParams.path, routerParams.recipient, routerParams.amountOut, routerParams.amountInMaximum],
            [handlerParams.aniAmount, handlerParams.usdqThreshold],
            inputCurrency.isNative,
          ])

          return {
            calldata: encoded,
            error: null,
            nativeValue: BigNumber.from(0),
          }
        } else if (aniQuote.quotedFromInput) {
          // User typed TOKEN amount (A.2 per Florian's spec)
          // Per spec: ALWAYS use swapTokenForExactInputANI
          // - DEX uses EXACT_OUTPUT (hyUSD is exact, token input has slippage)
          // - Slippage applies to INPUT (amountInMaximum)

          // Validate tokenAmount is available
          if (!aniQuote.tokenAmount) {
            return { ...invalidResult, error: 'Missing token amount for swap' }
          }

          // Build reversed path for EXACT_OUTPUT (hyUSD <- inputToken)
          let path: string
          if (aniQuote.dexRoute) {
            // encodeRouteToPath: true = exactOutput (reversed path)
            path = encodeRouteToPath(aniQuote.dexRoute, true)
          } else if (inputTokenAddr) {
            path = encodePath([hyUsdAddr, inputTokenAddr], [FeeAmount.MEDIUM])
          } else {
            return {
              ...invalidResult,
              error: 'Could not determine input token address',
            }
          }

          // Calculate ANI amount with safety margin (since ANI is calculated, not user-specified)
          const baseAniAmount = BigNumber.from(aniQuote.aniAmount.quotient.toString())
          const aniAmountForHandler = baseAniAmount.mul(ANI_SAFETY_MARGIN_PERCENT).div(ANI_SAFETY_MARGIN_DIVISOR)

          const handlerParams = {
            aniAmount: aniAmountForHandler,
            usdqThreshold,
          }

          // hyUSD amount is exact (target from quote)
          const hyUsdForHandler = BigNumber.from(aniQuote.hyUsdAmount.quotient.toString())

          // Calculate max token input with slippage (user may pay up to this amount)
          const baseTokenAmount = BigNumber.from(aniQuote.tokenAmount.quotient.toString())
          const slippageMultiplier = SLIPPAGE_BASE_POINTS + slippageBps
          const amountInMaximum = baseTokenAmount.mul(Math.floor(slippageMultiplier)).div(SLIPPAGE_BASE_POINTS)

          // ExactOutputParams: path, recipient, amountOut, amountInMaximum
          const routerParams = {
            path,
            recipient: adapterAddr,
            amountOut: hyUsdForHandler,
            amountInMaximum,
          }

          const encoded = iface.encodeFunctionData('swapTokenForExactInputANI', [
            [routerParams.path, routerParams.recipient, routerParams.amountOut, routerParams.amountInMaximum],
            [handlerParams.aniAmount, handlerParams.usdqThreshold],
            inputCurrency.isNative,
          ])

          return {
            calldata: encoded,
            error: null,
            // For native input, send the max token amount as value (excess refunded)
            nativeValue: inputCurrency.isNative ? amountInMaximum : BigNumber.from(0),
          }
        } else {
          // User typed ANI amount - use swapTokenForExactInputANI (EXACT_OUTPUT)
          // ANI amount is exact (user-specified), token input may increase within slippage
          // This guarantees the user gets exactly the ANI amount they specified

          // Validate tokenAmount is available
          if (!aniQuote.tokenAmount) {
            return { ...invalidResult, error: 'Missing token amount for swap' }
          }

          // Build reversed path for EXACT_OUTPUT (hyUSD <- inputToken)
          let path: string
          if (aniQuote.dexRoute) {
            // encodeRouteToPath: true = exactOutput (reversed path)
            path = encodeRouteToPath(aniQuote.dexRoute, true)
          } else if (inputTokenAddr) {
            // For exact output, path is reversed: hyUSD -> inputToken (but encoded output-first)
            path = encodePath([hyUsdAddr, inputTokenAddr], [FeeAmount.MEDIUM])
          } else {
            return {
              ...invalidResult,
              error: 'Could not determine input token address',
            }
          }

          // Use EXACT ANI amount - no safety margin since user specified this amount
          const exactAniAmount = BigNumber.from(aniQuote.aniAmount.quotient.toString())

          const handlerParams = {
            aniAmount: exactAniAmount,
            usdqThreshold,
          }

          // hyUSD amount is exact (what we need to buy the exact ANI)
          const hyUsdForHandler = BigNumber.from(aniQuote.hyUsdAmount.quotient.toString())

          // Calculate max token input with slippage (user may pay more, excess refunded)
          const baseTokenAmount = BigNumber.from(aniQuote.tokenAmount.quotient.toString())
          const slippageMultiplier = SLIPPAGE_BASE_POINTS + slippageBps
          const amountInMaximum = baseTokenAmount.mul(Math.floor(slippageMultiplier)).div(SLIPPAGE_BASE_POINTS)

          // ExactOutputParams: path, recipient, amountOut, amountInMaximum
          const routerParams = {
            path,
            recipient: adapterAddr,
            amountOut: hyUsdForHandler,
            amountInMaximum,
          }

          const encoded = iface.encodeFunctionData('swapTokenForExactInputANI', [
            [routerParams.path, routerParams.recipient, routerParams.amountOut, routerParams.amountInMaximum],
            [handlerParams.aniAmount, handlerParams.usdqThreshold],
            inputCurrency.isNative,
          ])

          return {
            calldata: encoded,
            error: null,
            // For native input, send the max token amount as value (excess refunded)
            nativeValue: inputCurrency.isNative ? amountInMaximum : BigNumber.from(0),
          }
        }
      } else {
        // SELL ANI: ANI -> hyUSD -> Token
        // Per Florian's spec: ALWAYS use swapANIForExactInputToken
        // - Handler forwards exact ANI to ANI contract, gets hyUSD back
        // - usdqThreshold = 0 (handler passes full hyUSD to DEX)
        // - DEX uses exact INPUT (hyUSD amount - buffer)
        // - Slippage applies to OUTPUT (amountOutMinimum)
        // - Adapter overwrites hyUSD amount based on actual received from ANI

        // Build DEX path (hyUSD -> token) - empty if direct hyUSD swap
        let path: string
        if (aniQuote.isDirectSwap) {
          path = emptyPath()
        } else if (aniQuote.dexRoute) {
          // Use the actual DEX route for multi-hop swaps
          // For SELL, DEX leg is exactInput (hyUSD -> token), so exactOutput = false
          path = encodeRouteToPath(aniQuote.dexRoute, false)
        } else {
          // Fallback: simple single-hop path
          // For native HYDRA output, use WHYDRA address in path (adapter unwraps it)
          const outputTokenAddr = outputCurrency.isNative ? wrappedNative?.address : (outputCurrency as Token).address

          if (!outputTokenAddr) {
            return {
              ...invalidResult,
              error: 'Could not determine output token address',
            }
          }

          // Default to 0.3% fee tier
          path = encodePath([hyUsdAddr, outputTokenAddr], [FeeAmount.MEDIUM])
        }

        // Calculate hyUSD for DEX and min output
        let hyUsdForDex: BigNumber
        let amountOutMinimum: BigNumber

        if (aniQuote.isDirectSwap) {
          // Direct swap: ANI -> hyUSD, no DEX leg needed
          hyUsdForDex = BigNumber.from(0)
          amountOutMinimum = BigNumber.from(0)
        } else {
          // Non-direct swap: need to swap hyUSD -> token via DEX
          // Per Florian: use hyUSD - buffer for DEX
          const hyUsdMinusBuffer = BigNumber.from(aniQuote.hyUsdAmount.quotient.toString()).sub(
            BigNumber.from(HYUSD_BUFFER)
          )
          hyUsdForDex = hyUsdMinusBuffer.gt(0) ? hyUsdMinusBuffer : BigNumber.from(0)

          // Calculate min output with slippage (slippage applies to OUTPUT for SELL)
          if (aniQuote.tokenAmount) {
            const slippageBps =
              (Number(slippageTolerance.numerator.toString()) * SLIPPAGE_BASE_POINTS) /
              Number(slippageTolerance.denominator.toString())
            const slippageMultiplier = SLIPPAGE_BASE_POINTS - slippageBps
            amountOutMinimum = BigNumber.from(aniQuote.tokenAmount.quotient.toString())
              .mul(Math.floor(slippageMultiplier))
              .div(SLIPPAGE_BASE_POINTS)
          } else {
            amountOutMinimum = BigNumber.from(0)
          }
        }

        // RouterParams for exact input (hyUSD -> token)
        const routerParams = {
          path,
          recipient: recipientAddr, // User receives final token
          amountIn: hyUsdForDex,
          amountOutMinimum,
        }

        // HandlerParams
        // Per Florian: usdqThreshold = 0 for SELL (handler passes full hyUSD to DEX)
        const handlerParams = {
          aniAmount: BigNumber.from(aniQuote.aniAmount.quotient.toString()),
          usdqThreshold: BigNumber.from(0),
        }

        const encoded = iface.encodeFunctionData('swapANIForExactInputToken', [
          [routerParams.path, routerParams.recipient, routerParams.amountIn, routerParams.amountOutMinimum],
          [handlerParams.aniAmount, handlerParams.usdqThreshold],
          outputCurrency.isNative,
        ])

        return {
          calldata: encoded,
          error: null,
          // SELL doesn't send native value, adapter sends native back to user
          nativeValue: BigNumber.from(0),
        }
      }
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Unknown error'
      console.error('Error building ANI swap calldata:', {
        error: e,
        direction: aniQuote.direction,
        isDirectSwap: aniQuote.isDirectSwap,
      })
      // Provide more specific error messages based on error type
      let userFriendlyError = 'Failed to build transaction'
      if (errorMessage.includes('path')) {
        userFriendlyError = 'Invalid swap path configuration'
      } else if (errorMessage.includes('amount')) {
        userFriendlyError = 'Invalid swap amount'
      } else if (errorMessage.includes('address')) {
        userFriendlyError = 'Invalid token address'
      }
      return { ...invalidResult, error: userFriendlyError }
    }
  }, [aniQuote, inputCurrency, outputCurrency, account, recipient, deadline, slippageTolerance, chainId])

  // Build callback
  const callback = useMemo(() => {
    if (!adapterContract || !calldata || error) {
      return null
    }

    return async (): Promise<string> => {
      // Use nativeValue for native HYDRA swaps (adapter wraps/unwraps automatically)
      const value = nativeValue

      // Estimate gas before swap - this validates the transaction will succeed
      const adapterAddr = ANI_ADAPTER_ADDRESSES[SupportedChainId.HYDRA]
      const estimatedGas = await adapterContract.signer.estimateGas({
        to: adapterAddr,
        data: calldata,
        value,
      })

      const gasLimit = estimatedGas.mul(GAS_BUFFER_PERCENT).div(GAS_BUFFER_DIVISOR)

      // Send transaction
      const tx = await adapterContract.signer.sendTransaction({
        to: adapterAddr,
        data: calldata,
        value,
        gasLimit,
      })

      // Add to transaction history
      // Use currencyId() utility for proper ID format ('ETH' for native)
      const inputId = inputCurrency ? currencyId(inputCurrency) : ''
      const outputId = outputCurrency ? currencyId(outputCurrency) : ''

      // Determine amounts based on swap direction
      const inputAmountRaw =
        aniQuote.direction === 'buy'
          ? aniQuote.tokenAmount?.quotient.toString() ?? '0'
          : aniQuote.aniAmount?.quotient.toString() ?? '0'
      const outputAmountRaw =
        aniQuote.direction === 'buy'
          ? aniQuote.aniAmount?.quotient.toString() ?? '0'
          : aniQuote.tokenAmount?.quotient.toString() ?? '0'

      // Use EXACT_INPUT format for transaction display
      addTransaction(tx, {
        type: TransactionType.SWAP,
        tradeType: TradeType.EXACT_INPUT,
        inputCurrencyId: inputId,
        inputCurrencyAmountRaw: inputAmountRaw,
        expectedOutputCurrencyAmountRaw: outputAmountRaw,
        outputCurrencyId: outputId,
        minimumOutputCurrencyAmountRaw: '0',
      })

      return tx.hash
    }
  }, [adapterContract, calldata, error, inputCurrency, outputCurrency, aniQuote, addTransaction, nativeValue])

  // Determine state
  const state = useMemo(() => {
    if (!account || chainId !== SupportedChainId.HYDRA) {
      return AniSwapCallbackState.INVALID
    }
    if (!adapterContract || !calldata) {
      return AniSwapCallbackState.LOADING
    }
    if (error) {
      return AniSwapCallbackState.INVALID
    }
    return AniSwapCallbackState.VALID
  }, [account, chainId, adapterContract, calldata, error])

  return {
    state,
    callback,
    error,
  }
}
